/**
 * bs-logo-spin — header lockup behaviour.
 *
 *  • Resting state is a crisp single-frame webp set in CSS. The 6×6 sprite
 *    sheet (assets/bs-logo-lockup.webp) is swapped in only for the burst
 *    and the homepage intro (class .bs-logo--spriting), then reverted —
 *    the swap is invisible (same crop, same box, sprite is preloaded).
 *  • Page-load response: one full, slower turn (frame 0 → 36 → 0,
 *    ~1200ms, ~30fps — the old 500ms/36-frame version needed ~72fps and
 *    skipped frames). Starts after first-contentful-paint + ~300ms; if
 *    the sprite is not yet decoded by then it is skipped outright (no
 *    fallback). Never on the homepage intro run; never under
 *    prefers-reduced-motion.
 *  • Homepage intro: scoped to the hero, not a full-screen overlay. The
 *    flyer (sections/bs-hero-v2.liquid, .bs-hero__intro-flyer) sits
 *    centred in the hero's own static, which is already running normally
 *    — nothing here manages that noise, and nothing on the page is
 *    hidden or blocked while this runs. Sequence: appear → one slow turn
 *    → fly into the header via FLIP against the real header element's
 *    getBoundingClientRect(). Total pace from settings.intro_duration
 *    (data-intro-duration), ~1200-1500ms by default. The header logo
 *    slot stays empty (<html>.bs-intro-pending) until the flyer docks.
 *  • Hide-on-scroll: the sticky bar slides out on scroll-down, back on
 *    scroll-up; held visible while the intro runs. Runs under
 *    prefers-reduced-motion too (just without the CSS transition).
 *  • prefers-reduced-motion: no intro, no burst — the header logo is
 *    just there from the first frame (see layout/theme.liquid, which
 *    never adds .bs-intro-pending in that case).
 */
(function () {
  'use strict';

  var FRAMES = 36;
  var COLS = 6;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var link = document.querySelector('[data-bs-logo]');
  var mark = document.querySelector('[data-bs-logo-lockup]');
  var flyer = document.querySelector('[data-bs-intro-flyer]');       // homepage only, may be absent
  var flyerLockup = flyer && flyer.querySelector('[data-bs-intro-lockup]');
  var heroTop = document.querySelector('.bs-hero-v2__top');          // for the scroll-abort check
  var headerEl = document.querySelector('.section-header') ||
                 document.querySelector('[data-bs-header]');
  var root = document.documentElement;

  function setFrame(el, idx) {
    idx = ((Math.round(idx) % FRAMES) + FRAMES) % FRAMES;
    if (el._bsF === idx) return;
    el._bsF = idx;
    el.style.setProperty('--bs-logo-fx', String(idx % COLS));
    el.style.setProperty('--bs-logo-fy', String(Math.floor(idx / COLS)));
  }

  function clearPending() { root.classList.remove('bs-intro-pending'); }

  /* ── Hide-on-scroll ───────────────────────────────────────────────────
     Independent of the intro/burst and of reduced-motion. Held off while
     the intro is running so the flying logo always has a header to land on. */
  var introActive = !!(flyer && root.classList.contains('bs-intro-pending'));
  (function hideOnScroll() {
    var hdr = document.querySelector('[data-bs-header]');
    if (!hdr || hdr.getAttribute('data-hide-on-scroll') === 'false') return;
    var target = headerEl || hdr;
    var TOP_GUARD = 100;                 // no hiding in the first 100px
    var UP_EPS = 2;                      // any upward move ≥ 2px reveals
    var lastY = window.pageYOffset || 0;
    var pendingY = lastY;
    var ticking = false;
    var tucked = false;

    function apply() {
      ticking = false;
      var y = pendingY;
      var want;
      if (introActive || y <= TOP_GUARD) {
        want = false;
      } else if (y > lastY + 1) {
        want = true;                     // scrolling down
      } else if (y < lastY - UP_EPS) {
        want = false;                    // scrolling up
      } else {
        want = tucked;                   // negligible move → keep state
      }
      if (want !== tucked) {
        tucked = want;
        target.classList.toggle('bs-header--tucked', tucked);
      }
      lastY = y;
    }
    window.addEventListener('scroll', function () {
      pendingY = window.pageYOffset || 0;
      if (!ticking) { ticking = true; requestAnimationFrame(apply); }
    }, { passive: true });
  })();

  /* ── Logo motion (skipped entirely under reduced-motion) ── */

  if (mark) setFrame(mark, 0);

  if (reduced) {
    introActive = false;
    clearPending();
    return;
  }

  var cfg = {
    burst: !link || link.getAttribute('data-burst') !== 'false',
    intro: !link || link.getAttribute('data-intro') !== 'false'
  };

  function easeInOutCubic(p) { return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2; }
  /* Mostly linear, with a light ease-out folded in only near the end —
     an even pace for most of the turn, a soft (not abrupt) stop. */
  function easeMostlyLinear(p) { return 0.8 * p + 0.2 * (1 - (1 - p) * (1 - p)); }

  /* ── Page-load response: one full, slower turn ──
     Frame 0 → 36 (wraps to 0) over BURST_DUR. At 1200ms that is ~33ms
     per frame (~30fps) — comfortably inside the refresh rate. The
     previous 500ms/36-frame version needed ~72fps and skipped frames;
     that is the whole reason this is slower. Ends on an exact frame 0. */
  var BURST_DUR = 1200;   // do not go below ~1000ms — see call site comment
  function spinHeader(dur) {
    mark.classList.add('bs-logo--spriting');
    var t0 = performance.now();
    (function step(now) {
      var p = Math.min(1, (now - t0) / dur);
      setFrame(mark, easeMostlyLinear(p) * FRAMES);
      if (p < 1) {
        requestAnimationFrame(step);
      } else {
        setFrame(mark, 0);                    // exact frame 0, static
        requestAnimationFrame(function () { mark.classList.remove('bs-logo--spriting'); });
      }
    })(t0);
  }

  /* ── Homepage intro ── */
  if (flyer && flyerLockup && mark && cfg.intro && root.classList.contains('bs-intro-pending')) {
    runIntro();
  } else {
    introActive = false;
    clearPending();

    if (mark && cfg.burst) {
      /* Decode the sprite in the background — never block on it. */
      var burstSpriteOk = false;
      (function () {
        var src = mark.getAttribute('data-sprite');
        if (!src) return;
        var img = new Image();
        img.src = src;
        if (img.decode) { img.decode().then(function () { burstSpriteOk = true; }, function () {}); }
        else { img.onload = function () { burstSpriteOk = true; }; }
      })();

      /* Start only after the first contentful paint, then an offset so the
         critical rendering path is clear. Bumped from 150ms to 300ms for
         this slower (1200ms) turn: the animation now spans much more of
         the post-FCP window, so a bit more headroom before it starts
         further reduces any chance of competing with trailing layout/font
         work. If the sprite still is not decoded by then: skip. No wait,
         no catch-up — the static logo is the intended state. */
      var burstFired = false;
      function fireBurst() {
        if (burstFired) return;
        burstFired = true;
        setTimeout(function () { if (burstSpriteOk) spinHeader(BURST_DUR); }, 300);
      }
      if (window.PerformanceObserver) {
        try {
          var po = new PerformanceObserver(function (list) {
            var e = list.getEntries();
            for (var i = 0; i < e.length; i++) {
              if (e[i].name === 'first-contentful-paint') { po.disconnect(); fireBurst(); return; }
            }
          });
          po.observe({ type: 'paint', buffered: true });
        } catch (e) { /* fall through to the timeout */ }
      }
      setTimeout(fireBurst, 1200);   // safety if no FCP entry surfaces
    }
  }

  /* ================================================================== */

  function runIntro() {
    try { sessionStorage.setItem('bsIntroDone', '1'); } catch (e) {}

    setFrame(flyerLockup, 0);
    introActive = true;

    /* Pace: three phases (appear, one turn, fly to header) summing to
       settings.intro_duration (data-intro-duration), default ~1300ms —
       roughly half the old full-screen version, since nothing here needs
       to wait for a separate full-screen canvas or a docking transition
       any more (see B4 in the round that scoped this to the hero: the
       static was already at its final size/position, so there was no
       full-screen-to-hero handoff left to choreograph around). */
    var D = parseInt(link && link.getAttribute('data-intro-duration'), 10);
    if (!(D >= 1000 && D <= 2500)) D = 1300;
    var k = D / 1300;
    var APPEAR_DUR = 250 * k;
    var ROTATE_DUR = 550 * k;
    var FLY_DUR = 500 * k;
    var DECODE_MAX_WAIT = 250;   // fixed, small — not part of the pace budget

    var start = performance.now();
    var aborted = false, finished = false;
    var phase = 0, phaseAt = 0;   // 0 = waiting on decode, 1 = appear, 2 = rotate, 3 = fly
    var spriteReady = false;

    /* ── sprite decode gate: the flyer never appears before the sprite is
       decoded — img.decode(), not just onload — capped at DECODE_MAX_WAIT
       so a slow decode cannot stall the intro. ── */
    (function decodeSprite() {
      var src = (mark && mark.getAttribute('data-sprite')) || '';
      if (!src) {
        var m = /url\(["']?([^"')]+)/.exec(flyerLockup.style.backgroundImage || '');
        if (m) src = m[1];
      }
      function done() { spriteReady = true; }
      if (!src) { done(); return; }
      var img = new Image();
      img.src = src;
      if (img.decode) {
        img.decode().then(done, function () { img.complete ? done() : (img.onload = img.onerror = done); });
      } else {
        img.onload = img.onerror = done;
      }
    })();

    /* ── Scroll-abort: if the hero scrolls out of view while the intro is
       still running, finish it instantly instead of animating something
       nobody can see — the header logo just appears, no fly-out. ── */
    function heroInView() {
      if (!heroTop) return true;   // no hero on this page somehow — don't block on it
      var r = heroTop.getBoundingClientRect();
      return r.bottom > 0 && r.top < (window.innerHeight || document.documentElement.clientHeight);
    }
    function onScroll() {
      if (aborted || finished) return;
      if (!heroInView()) abort();
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    requestAnimationFrame(tick);

    function tick(now) {
      if (aborted || finished) return;
      var t = now - start;

      if (phase === 0) {
        if (spriteReady || t >= DECODE_MAX_WAIT) {
          phase = 1; phaseAt = t;
          flyer.style.transition = 'opacity ' + Math.round(APPEAR_DUR) + 'ms linear';
          flyer.style.opacity = '1';
        }
      } else if (phase === 1) {
        if (t >= phaseAt + APPEAR_DUR) {
          phase = 2; phaseAt = t;
        }
      } else if (phase === 2) {
        var p = Math.min(1, (t - phaseAt) / ROTATE_DUR);
        setFrame(flyerLockup, easeInOutCubic(p) * FRAMES);   // exactly one turn, lands on frame 0
        if (p >= 1) {
          phase = 3; phaseAt = t;
          startFlight(FLY_DUR);
        }
      }

      requestAnimationFrame(tick);
    }

    /* ── FLIP: fly the flyer from its resting spot in the hero static to
       the real header lockup's current position, via a single transform
       (translate + uniform scale — both boxes share the same 2.41:1
       aspect ratio, so scaling by width alone keeps it undistorted). ── */
    function flyerTransform(to) {
      var from = flyer.getBoundingClientRect();
      if (!to || !to.width || !from.width) return null;
      var s = to.width / from.width;
      var dx = (to.left + to.width / 2) - (from.left + from.width / 2);
      var dy = (to.top + to.height / 2) - (from.top + from.height / 2);
      return 'translate(-50%, -50%) translate(' + dx + 'px, ' + dy + 'px) scale(' + s + ')';
    }

    function startFlight(dur) {
      dur = Math.max(160, dur || 0);
      var ft = flyerTransform(mark.getBoundingClientRect());
      var ease = 'cubic-bezier(0.65,0,0.35,1)';
      if (ft) {
        flyer.style.transition = 'transform ' + dur + 'ms ' + ease;
        void flyer.offsetWidth;
        flyer.style.transform = ft;
      }
      setTimeout(dock, dur);
      setTimeout(finish, dur + 60);
    }

    /* swap the real header logo in exactly as the flyer lands — one frame
       of pixel-coincident overlap, then the flyer is gone. */
    function dock() {
      if (finished) return;
      requestAnimationFrame(function () {
        clearPending();
        flyer.style.opacity = '0';
        requestAnimationFrame(finish);
      });
    }

    function abort() {
      if (aborted || finished) return;
      aborted = true;
      window.removeEventListener('scroll', onScroll);
      /* Hero is out of view — nothing to animate into. Cut straight to
         the end state: header logo on, flyer gone, header never hidden. */
      clearPending();
      flyer.style.transition = 'none';
      flyer.style.opacity = '0';
      finish();
    }

    function finish() {
      if (finished) return;
      finished = true;
      introActive = false;
      window.removeEventListener('scroll', onScroll);
      clearPending();
      if (flyer && flyer.parentNode) flyer.parentNode.removeChild(flyer);
    }
  }
})();
